// client/src/shared/lib/debug.ts
// Helper debug conditionnel — activé par localStorage.MB_DEBUG = 'true'
// Usage : dbg('WS', 'RECV message:partial sid=...', '42 bytes')
// Usage étendu : dbgEvent('WS', 'RECV message:partial', { sessionId, chunk })

// --- Types ---

export type DebugSource = 'WS' | 'Bus' | 'RQ' | 'Zustand' | 'Mutation' | 'Error'

export interface DebugEvent {
  ts: string       // ISO timestamp HH:mm:ss.SSS
  source: DebugSource
  label: string    // ex: "← message:partial"
  payload?: unknown
}

// --- Config ---

const BUFFER_MAX = 200

// Couleurs par source (console %c)
const SOURCE_COLORS: Record<DebugSource, string> = {
  WS: '#2196F3',        // bleu
  Bus: '#9C27B0',       // violet
  RQ: '#4CAF50',        // vert
  Zustand: '#FF9800',   // orange
  Mutation: '#F44336',  // rouge
  Error: '#FF0000',     // rouge vif
}

// --- Buffer circulaire ---

const DEBUG_BUFFER: DebugEvent[] = []

function isDebugMode(): boolean {
  try {
    return localStorage.getItem('MB_DEBUG') === 'true'
  } catch {
    return false
  }
}

// --- API publique ---

/**
 * Log debug simple (rétrocompatible avec l'ancienne API).
 * Accepte window.__MB_DEBUG pour rétrocompatibilité.
 */
export function dbg(actor: string, ...args: unknown[]): void {
  const legacyFlag = (window as unknown as Record<string, unknown>).__MB_DEBUG
  if (!legacyFlag && !isDebugMode()) return
  const ts = new Date().toISOString().slice(11, 23)
  console.debug(`[MB:DEBUG] ${ts} [${actor}]`, ...args)
}

/**
 * Log debug structuré avec source colorée et stockage dans le buffer circulaire.
 */
export function dbgEvent(source: DebugSource, label: string, payload?: unknown): void {
  if (!isDebugMode()) return

  const ts = new Date().toISOString().slice(11, 23)
  const event: DebugEvent = { ts, source, label, payload }

  // Ajout au buffer circulaire — on garde les BUFFER_MAX derniers events
  if (DEBUG_BUFFER.length >= BUFFER_MAX) {
    DEBUG_BUFFER.shift()
  }
  DEBUG_BUFFER.push(event)

  // Console colorée
  const color = SOURCE_COLORS[source]
  console.debug(
    `%c[${source}]%c ${ts} ${label}`,
    `color: ${color}; font-weight: bold`,
    'color: inherit; font-weight: normal',
    ...(payload !== undefined ? [payload] : []),
  )
}

/**
 * Retourne une copie du buffer circulaire.
 */
export function getDebugBuffer(): DebugEvent[] {
  return [...DEBUG_BUFFER]
}

/**
 * Vide le buffer circulaire.
 */
export function clearDebugBuffer(): void {
  DEBUG_BUFFER.length = 0
}

/**
 * Indique si le mode debug est actif.
 */
export { isDebugMode }
